import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, Zap, Clock } from 'lucide-react';
import ComponentIdBadge from '../../../components/ui/ComponentIdBadge';
import { supabase } from '../../../lib/supabase';

export default function SupabaseConnectivityPanel({ onHealthUpdate, triggerCheck }) {
  const [expanded, setExpanded] = useState(false);
  const [checking, setChecking] = useState(false);
  const [supabaseHealth, setSupabaseHealth] = useState({
    status: 'checking',
    checks: {
      databaseConnection: { status: 'pending', message: '', latency: 0 },
      apiEndpoint: { status: 'pending', message: '', latency: 0 },
      authService: { status: 'pending', message: '', latency: 0 },
      realtimeService: { status: 'pending', message: '', latency: 0 }
    },
    issues: [],
    averageLatency: 0,
    config: {
      url: '',
      hasAnonKey: false,
      region: 'unknown'
    }
  });

  useEffect(() => {
    if (triggerCheck) {
      performSupabaseCheck();
    }
  }, [triggerCheck]);

  const performSupabaseCheck = async () => {
    setChecking(true);
    const issues = [];
    const checks = { ...supabaseHealth?.checks };
    const latencies = [];

    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      // Configuration Check
      if (!supabaseUrl || !supabaseAnonKey) {
        issues?.push('Missing Supabase environment variables');
        throw new Error('Supabase not configured');
      }

      // Database Connection Check
      const dbStart = performance.now();
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('id')?.limit(1);
        
        const dbLatency = Math.round(performance.now() - dbStart);
        latencies?.push(dbLatency);

        if (error) {
          checks.databaseConnection = {
            status: 'error',
            message: `Connection failed: ${error?.message}`,
            latency: dbLatency
          };
          issues?.push('Database connection error');
        } else {
          checks.databaseConnection = {
            status: 'healthy',
            message: 'Database connection successful',
            latency: dbLatency
          };
        }
      } catch (dbError) {
        checks.databaseConnection = {
          status: 'error',
          message: `Connection failed: ${dbError?.message}`,
          latency: 0
        };
        issues?.push('Database unreachable');
      }

      // API Endpoint Check
      const apiStart = performance.now();
      try {
        const { data, error } = await supabase?.from('system_roles')?.select('id')?.limit(1);
        
        const apiLatency = Math.round(performance.now() - apiStart);
        latencies?.push(apiLatency);

        if (error) {
          checks.apiEndpoint = {
            status: 'warning',
            message: `API accessible but query failed: ${error?.message}`,
            latency: apiLatency
          };
        } else {
          checks.apiEndpoint = {
            status: 'healthy',
            message: 'API endpoint responding correctly',
            latency: apiLatency
          };
        }
      } catch (apiError) {
        checks.apiEndpoint = {
          status: 'error',
          message: `API endpoint unreachable: ${apiError?.message}`,
          latency: 0
        };
        issues?.push('API endpoint unavailable');
      }

      // Auth Service Check
      const authStart = performance.now();
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession();
        const authLatency = Math.round(performance.now() - authStart);
        latencies?.push(authLatency);

        if (error) {
          checks.authService = {
            status: 'warning',
            message: `Auth service error: ${error?.message}`,
            latency: authLatency
          };
        } else {
          checks.authService = {
            status: 'healthy',
            message: session ? 'Auth service active (user authenticated)' : 'Auth service active (no session)',
            latency: authLatency
          };
        }
      } catch (authError) {
        checks.authService = {
          status: 'error',
          message: `Auth service unavailable: ${authError?.message}`,
          latency: 0
        };
        issues?.push('Authentication service down');
      }

      // Realtime Service Check
      const realtimeStart = performance.now();
      try {
        const channel = supabase?.channel('health-check-test');
        const realtimeLatency = Math.round(performance.now() - realtimeStart);
        latencies?.push(realtimeLatency);
        
        checks.realtimeService = {
          status: 'healthy',
          message: 'Realtime service initialized successfully',
          latency: realtimeLatency
        };
        
        // Clean up test channel
        supabase?.removeChannel(channel);
      } catch (realtimeError) {
        checks.realtimeService = {
          status: 'warning',
          message: 'Realtime service may be unavailable',
          latency: 0
        };
      }

      // Calculate average latency
      const avgLatency = latencies?.length > 0 
        ? Math.round(latencies?.reduce((a, b) => a + b, 0) / latencies?.length)
        : 0;

      // Determine overall status
      const hasErrors = Object.values(checks)?.some(c => c?.status === 'error');
      const hasWarnings = Object.values(checks)?.some(c => c?.status === 'warning');
      const overallStatus = hasErrors ? 'error' : hasWarnings ? 'warning' : 'healthy';

      const updatedHealth = {
        status: overallStatus,
        checks,
        issues,
        averageLatency: avgLatency,
        config: {
          url: supabaseUrl,
          hasAnonKey: !!supabaseAnonKey,
          region: supabaseUrl?.includes('supabase.co') ? supabaseUrl?.split('.')?.[0]?.split('//')?.[1] : 'custom'
        }
      };

      setSupabaseHealth(updatedHealth);
      onHealthUpdate?.(updatedHealth);
    } catch (error) {
      console.error('Supabase health check error:', error);
      const errorHealth = {
        status: 'error',
        checks: {
          databaseConnection: { status: 'error', message: 'Check failed', latency: 0 },
          apiEndpoint: { status: 'error', message: 'Check failed', latency: 0 },
          authService: { status: 'error', message: 'Check failed', latency: 0 },
          realtimeService: { status: 'error', message: 'Check failed', latency: 0 }
        },
        issues: ['Health check failed - Supabase may not be configured'],
        averageLatency: 0,
        config: supabaseHealth?.config
      };
      setSupabaseHealth(errorHealth);
      onHealthUpdate?.(errorHealth);
    } finally {
      setChecking(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      default: return <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />;
    }
  };

  const getLatencyColor = (latency) => {
    if (latency === 0) return 'text-gray-500';
    if (latency < 100) return 'text-green-600';
    if (latency < 300) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative">
      <ComponentIdBadge
        id="cmp-supabase-connectivity-panel"
        name="Supabase Connectivity Panel"
        description="Database, API, auth, and realtime service health monitoring"
      />
      
      {/* Panel Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Database className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Supabase Connectivity</h3>
              <p className="text-sm text-gray-600">Database connection, API endpoint, and service availability</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right mr-2">
              <div className={`text-2xl font-bold ${getLatencyColor(supabaseHealth?.averageLatency)}`}>
                {supabaseHealth?.averageLatency}ms
              </div>
              <div className="text-xs text-gray-500">Avg Latency</div>
            </div>
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(supabaseHealth?.status)} bg-opacity-10`}>
              {getStatusIcon(supabaseHealth?.status)}
              {supabaseHealth?.status === 'healthy' && 'Healthy'}
              {supabaseHealth?.status === 'warning' && 'Warning'}
              {supabaseHealth?.status === 'error' && 'Error'}
              {supabaseHealth?.status === 'checking' && 'Checking...'}
            </span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Panel Content */}
      {expanded && (
        <div className="p-6 space-y-4">
          {/* Configuration Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Configuration</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Supabase URL:</span>
                <span className="font-mono text-gray-900 text-xs">{supabaseHealth?.config?.url || 'Not configured'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Anon Key:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  supabaseHealth?.config?.hasAnonKey ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {supabaseHealth?.config?.hasAnonKey ? 'Configured' : 'Missing'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Region:</span>
                <span className="font-mono text-gray-900 text-xs">{supabaseHealth?.config?.region}</span>
              </div>
            </div>
          </div>

          {/* Service Health Checks */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Service Health</h4>
            
            {Object.entries(supabaseHealth?.checks)?.map(([key, check]) => (
              <div key={key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={getStatusColor(check?.status)}>
                  {getStatusIcon(check?.status)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium text-gray-900 capitalize">
                      {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                    </div>
                    {check?.latency > 0 && (
                      <div className={`flex items-center gap-1 text-xs font-medium ${getLatencyColor(check?.latency)}`}>
                        <Clock className="w-3 h-3" />
                        {check?.latency}ms
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-600">{check?.message}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Performance Metrics */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Performance Metrics
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-blue-700 font-medium">Average Latency</div>
                <div className={`text-2xl font-bold ${getLatencyColor(supabaseHealth?.averageLatency)}`}>
                  {supabaseHealth?.averageLatency}ms
                </div>
              </div>
              <div>
                <div className="text-blue-700 font-medium">Status</div>
                <div className="text-2xl font-bold text-blue-900 capitalize">
                  {supabaseHealth?.status}
                </div>
              </div>
            </div>
          </div>

          {/* Issues */}
          {supabaseHealth?.issues?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-red-900 mb-2">Connectivity Issues</h4>
              <ul className="space-y-1">
                {supabaseHealth?.issues?.map((issue, idx) => (
                  <li key={idx} className="text-sm text-red-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Troubleshooting */}
          {supabaseHealth?.status !== 'healthy' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-900 mb-2">Troubleshooting Steps</h4>
              <ul className="space-y-1 text-sm text-yellow-800">
                {!supabaseHealth?.config?.hasAnonKey && (
                  <li>• Check .env file for VITE_SUPABASE_ANON_KEY</li>
                )}
                {!supabaseHealth?.config?.url && (
                  <li>• Check .env file for VITE_SUPABASE_URL</li>
                )}
                {supabaseHealth?.checks?.databaseConnection?.status === 'error' && (
                  <li>• Verify Supabase project is active and accessible</li>
                )}
                {supabaseHealth?.averageLatency > 500 && (
                  <li>• High latency detected - check network connection or Supabase region</li>
                )}
                <li>• Verify CORS settings in Supabase dashboard allow current origin</li>
                <li>• Check Supabase project status at status.supabase.com</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}